import createClient, { type Middleware } from "openapi-fetch";

import type { paths } from "./generated/schema";
import { ProblemError, toProblemDetails } from "./problem";

const CORRELATION_HEADER = "X-Correlation-Id";

export interface ClientOptions {
  /** Base URL of the API, e.g. `/api` behind Caddy or `http://127.0.0.1:8000` in dev. */
  baseUrl: string;
  /** Supplies a correlation id per request so a UI action can be found in the server log. */
  correlationId?: () => string;
}

function defaultCorrelationId(): string {
  return crypto.randomUUID();
}

/**
 * The single way the web app talks to the backend (scaffold plan §6.4).
 * Errors surface as ProblemError; callers never inspect raw responses.
 */
export function createApiClient(options: ClientOptions) {
  const nextCorrelationId = options.correlationId ?? defaultCorrelationId;

  const errorMiddleware: Middleware = {
    async onRequest({ request }) {
      request.headers.set(CORRELATION_HEADER, nextCorrelationId());
      return request;
    },
    async onResponse({ response }) {
      if (response.ok) return response;

      const correlationId = response.headers.get(CORRELATION_HEADER);
      let payload: unknown = null;
      try {
        payload = await response.clone().json();
      } catch {
        payload = null;
      }
      throw new ProblemError(toProblemDetails(payload, response.status), correlationId);
    },
  };

  const client = createClient<paths>({ baseUrl: options.baseUrl });
  client.use(errorMiddleware);
  return client;
}

export type ApiClient = ReturnType<typeof createApiClient>;
